import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight } from "lucide-react";
import useAxisLockedSwipe from "@/lib/useAxisLockedSwipe";

/**
 * CollectionsPeekCarousel — single-dominant-card carousel with peeks of the
 * neighbours.
 *
 * Why JS-driven (and not native scroll-snap):
 *   We tried native overflow-x scroll-snap with `touch-action: pan-x`. It is
 *   inconsistent across Chrome on Android — a slightly-diagonal vertical
 *   swipe can lock the carousel into horizontal-only mode and freeze page
 *   scroll. Users hit this repeatedly on the home page. JS axis-locking with
 *   a transform-based slider eliminates the bug entirely.
 *
 * Visual behaviour:
 *   - Active card sits perfectly centred at every breakpoint.
 *   - Card width responsive: 78% mobile → 28% xl. Peek = (100 - card%) / 2.
 *   - Translate offset = activeIdx * (cardPct + gapPct), so neighbours stay
 *     visible on both sides.
 *   - Tap on a non-active peek scrolls it to centre. Tap on the active card
 *     opens its collection page (Airbnb pattern).
 *   - Side arrows wrap (loop) at the ends.
 */
const BREAKPOINTS = [
  { min: 0, cardPct: 78, gapPct: 4 }, // mobile
  { min: 640, cardPct: 64, gapPct: 4 }, // sm
  { min: 768, cardPct: 44, gapPct: 3 }, // md
  { min: 1024, cardPct: 32, gapPct: 2.5 }, // lg
  { min: 1280, cardPct: 28, gapPct: 2 }, // xl
];

const useResponsivePct = () => {
  const [bp, setBp] = useState(BREAKPOINTS[0]);
  useEffect(() => {
    const recalc = () => {
      const w = window.innerWidth;
      let chosen = BREAKPOINTS[0];
      for (const b of BREAKPOINTS) if (w >= b.min) chosen = b;
      setBp(chosen);
    };
    recalc();
    window.addEventListener("resize", recalc);
    return () => window.removeEventListener("resize", recalc);
  }, []);
  return bp;
};

export default function CollectionsPeekCarousel({ collections }) {
  const trackWrapRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [drag, setDrag] = useState(0);
  const N = collections?.length || 0;
  const { cardPct, gapPct } = useResponsivePct();

  // Each step = card + gap (the next card's left edge moves this much).
  const stepPct = cardPct + gapPct;
  // Centre offset — each card is centred when translate = -i*step + sideOffset.
  const sideOffsetPct = (100 - cardPct) / 2;

  useAxisLockedSwipe({
    elementRef: trackWrapRef,
    onSwipe: (dir) => setActiveIdx((cur) => (((cur + dir) % N) + N) % N),
    onDrag: setDrag,
  });

  const goTo = (i, e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    setActiveIdx(((i % N) + N) % N);
  };

  const onCardClick = (i, e) => {
    if (i !== activeIdx) {
      e.preventDefault();
      goTo(i);
    }
  };

  if (!N) return null;

  // translateX: position the active card so its CENTRE is at viewport centre.
  // start = sideOffset (so card 0 left edge = sideOffset of viewport).
  // translate at activeIdx i = sideOffset - i * step (everything shifts left).
  const tx = `calc(${sideOffsetPct - activeIdx * stepPct}% + ${drag}px)`;

  return (
    <div data-testid="collections-peek-carousel" className="relative select-none px-4 sm:px-12 lg:px-20">
      <div ref={trackWrapRef} className="overflow-hidden">
        <div
          data-testid="collections-peek-track"
          className="flex"
          style={{
            transform: `translate3d(${tx}, 0, 0)`,
            transition: drag === 0 ? "transform 420ms cubic-bezier(0.22, 1, 0.36, 1)" : "none",
            gap: `${gapPct}%`,
          }}
        >
          {collections.map((c, i) => {
            const isActive = i === activeIdx;
            return (
              <Link
                key={c.slug}
                to={`/collections/${c.slug}`}
                onClick={(e) => onCardClick(i, e)}
                data-active={isActive ? "yes" : "no"}
                data-testid={`collection-card-${c.slug}`}
                className={`shrink-0 overflow-hidden rounded-3xl bg-white shadow-card transition-all duration-500 ease-out ${
                  isActive ? "scale-100 opacity-100" : "scale-[0.88] opacity-65 hover:opacity-80"
                }`}
                style={{ width: `${cardPct}%` }}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={c.image}
                    alt={c.name}
                    loading="lazy"
                    draggable={false}
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="flex items-start justify-between gap-3 p-5">
                  <div className="flex-1">
                    <p className="text-[10px] uppercase tracking-[0.22em] text-amber">Collection</p>
                    <h3 className="mt-1 font-display text-lg leading-tight text-noir sm:text-xl">{c.name}</h3>
                    <p className="font-accent mt-0.5 text-sm text-forest/70">{c.tagline}</p>
                  </div>
                  <span className="shrink-0 rounded-full border border-forest/20 bg-mist/40 p-2 text-forest">
                    <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      <button
        onClick={(e) => goTo(activeIdx - 1, e)}
        data-testid="collections-peek-prev"
        className="absolute left-0 top-[42%] z-20 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full border border-forest/30 bg-forest text-parchment shadow-soft ring-2 ring-parchment transition hover:bg-forest/90 hover:scale-105 sm:left-2 lg:h-14 lg:w-14"
        aria-label="Previous collection"
      >
        <ChevronLeft size={22} />
      </button>
      <button
        onClick={(e) => goTo(activeIdx + 1, e)}
        data-testid="collections-peek-next"
        className="absolute right-0 top-[42%] z-20 -translate-y-1/2 inline-flex h-12 w-12 items-center justify-center rounded-full border border-forest/30 bg-forest text-parchment shadow-soft ring-2 ring-parchment transition hover:bg-forest/90 hover:scale-105 sm:right-2 lg:h-14 lg:w-14"
        aria-label="Next collection"
      >
        <ChevronRight size={22} />
      </button>

      <div className="mt-4 flex justify-center gap-1.5" data-testid="collections-peek-dots">
        {collections.map((c, i) => (
          <button
            key={c.slug}
            onClick={(e) => goTo(i, e)}
            data-testid={`collections-peek-dot-${i}`}
            aria-label={`Go to ${c.name}`}
            className={`h-1.5 rounded-full transition-all ${i === activeIdx ? "w-6 bg-forest" : "w-1.5 bg-forest/25 hover:bg-forest/50"}`}
          />
        ))}
      </div>
    </div>
  );
}
